export interface FindDegreesBenefit {
  icon:
    | 'GraduationCapIcon'
    | 'BookOpenIcon'
    | 'HeartIcon'
    | 'UsersIcon'
    | 'ShieldCheckIcon'
    | 'ClockIcon'
    | 'DollarSignIcon'
    | 'AwardIcon'
  title: string
  description: string
}

export interface FindDegreesTestimonial {
  quote: string
  name: string
  detail: string
}

export interface FindDegreesTemplate {
  slug: string
  metaTitle: string
  metaDescription: string
  // Hero
  eyebrow: string
  headline: string
  subheadline: string
  heroImage: string
  // New hero layout fields (optional for backward compat)
  heroEyebrowBadge?: string // small pill at top, e.g. "● NEXT SESSION STARTS SOON"
  heroHeadlinePrefix?: string // e.g. "Earn an"
  heroHeadlineHighlight?: string // e.g. "Online Degree" (rendered in accent red)
  heroHeadlineSuffix?: string // e.g. "On Your Schedule"
  heroCardHeading?: string // e.g. "Earn an Online Degree"
  heroCardSubtext?: string // e.g. "Next session starts soon."
  heroCardHelper?: string // e.g. helper line below CTA in the card
  heroStep1Label?: string // e.g. "Profile Complete"
  heroStep2Label?: string // e.g. "Confirm Email"
  // Primary CTA
  ctaLabel: string
  ctaHref: string
  // Quick trust strip
  trustItems: string[]
  // Value props / benefits
  benefitsHeading: string
  benefits: FindDegreesBenefit[]
  // Featured schools / logos (text only fallback)
  schoolsHeading: string
  schoolNames: string[]
  // Social proof
  testimonial?: FindDegreesTestimonial
  // Bottom CTA
  bottomCtaHeading: string
  bottomCtaBody: string
  bottomCtaLabel: string
  // Theme color
  accentColor: string
  accentColorDark: string
}

export const findDegreesTemplates: FindDegreesTemplate[] = [
  {
    slug: 'online-christian-colleges',
    metaTitle:
      'Find an Online Christian College That Fits Your Calling | OnlineCollegeGuide',
    metaDescription:
      'Discover accredited online Christian colleges and faith-based universities. Compare programs in ministry, business, nursing, counseling, and more.',
    eyebrow: 'Faith-Based Online Education',
    headline: 'Earn Your Degree at an Online Christian College',
    subheadline:
      'Flexible, accredited online Christian programs designed for working adults. Explore your options and take the first step toward a faith-rooted future today.',
    heroImage:
      'https://images.unsplash.com/photo-1507692049790-de58290a4334?w=900&q=80&auto=format&fit=crop',
    heroEyebrowBadge: 'NEXT SESSION STARTS SOON',
    heroHeadlinePrefix: 'Earn an',
    heroHeadlineHighlight: 'Online Christian Degree',
    heroHeadlineSuffix: 'On Your Schedule',
    heroCardHeading: 'Earn an Online Christian Degree',
    heroCardSubtext: 'Next session starts soon.',
    heroCardHelper:
      'Want to learn more? Our admissions team is here to support you and get your questions answered.',
    heroStep1Label: 'Profile Complete',
    heroStep2Label: 'Confirm Email',
    ctaLabel: 'Get Started',
    ctaHref: '/guide/online-christian-colleges',
    trustItems: [
      '100% Free Service',
      'Regionally Accredited Schools',
      'No Obligation',
      'Used by 2M+ Students',
    ],
    benefitsHeading: 'Why Choose an Online Christian College?',
    benefits: [
      {
        icon: 'BookOpenIcon',
        title: 'Faith-Integrated Learning',
        description:
          'Every course is taught from a biblical worldview — connect what you learn with what you believe.',
      },
      {
        icon: 'ClockIcon',
        title: 'Flexible for Working Adults',
        description:
          'Accelerated 8-week courses and asynchronous learning fit around work, family, and ministry.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited & Respected',
        description:
          'Earn a regionally accredited degree recognized by employers, grad schools, and licensing boards.',
      },
      {
        icon: 'DollarSignIcon',
        title: 'Affordable Tuition',
        description:
          'Discounted online rates, generous transfer credit, and scholarships for ministry and military.',
      },
      {
        icon: 'UsersIcon',
        title: 'Christian Community',
        description:
          'Connect with classmates and faculty who share your faith through virtual chapel and mentorship.',
      },
      {
        icon: 'AwardIcon',
        title: 'Programs for Every Calling',
        description:
          'From ministry and theology to nursing, counseling, business, and education — all faith-informed.',
      },
    ],
    schoolsHeading: 'Featured Christian Universities',
    schoolNames: [
      'Liberty University',
      'Grand Canyon University',
      'Regent University',
      'Colorado Christian University',
      'Indiana Wesleyan University',
      'Ohio Christian University',
    ],
    testimonial: {
      quote:
        "Going back to school felt impossible until I found an online Christian program that fit my life. I'm earning my degree while still leading worship at my church — and growing spiritually at the same time.",
      name: 'Sarah M.',
      detail: 'BA in Christian Ministry, Online Student',
    },
    bottomCtaHeading: 'Ready to Find Your Christian College?',
    bottomCtaBody:
      'Browse accredited online Christian colleges, compare programs, and take the next step toward a degree that aligns with your values.',
    bottomCtaLabel: 'Explore Christian Colleges',
    accentColor: '#1B365D', // navy
    accentColorDark: '#0F2440',
  },
]

export function getFindDegreesTemplate(
  slug: string,
): FindDegreesTemplate | undefined {
  const normalized = slug.toLowerCase()
  return findDegreesTemplates.find((t) => t.slug.toLowerCase() === normalized)
}
